import {
  SUBJECTS,
  TRACKER_HOSTS,
  ageDays,
  cap,
  fromNow,
  hostOf,
  humanizeFeature,
  harmDefault,
  humanizeLabel,
  humanizePolicy,
  labelWord,
  legitFor,
  numWord,
  STATUS_HELP,
  titleCase,
} from "./reviewUi";

// Plain-language summaries derived client-side from the case dossier.

export const allItems = (dossier) =>
  SUBJECTS.flatMap((s) => (dossier?.subjects?.[s.key] || []).map((item) => ({ ...item, subjectType: s.key })));

export const isOpenItem = (item) => (item.review?.status || "open") === "open";

// Hold mode: a blocked template waits here instead of going to Meta.
export const isHeld = (item) => !!item?.held && !item.held.releasedAt && !item.held.rejected;
export const heldCount = (dossier) => allItems(dossier || {}).filter((i) => isOpenItem(i) && isHeld(i)).length;
// Held and later rejected here (open or resolved): Dismiss can restore these.
export const restorableCount = (dossier) => allItems(dossier || {}).filter((i) => i.held?.outcome === "rejected").length;

const SEVERITY = { block: 3, review: 2, report: 1 };

// Default category for a suspension: the worst open item's label, else the last violation's.
export const caseWorstLabel = (dossier) => {
  const open = allItems(dossier || {})
    .filter(isOpenItem)
    .sort(
      (a, b) =>
        (SEVERITY[b.decision] || 0) - (SEVERITY[a.decision] || 0) || new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  const fromItems = open.map((i) => harmDefault(i.content?.primaryLabel)).find(Boolean);
  return fromItems || harmDefault(dossier?.lastViolation?.label);
};

// Default legit label for a dismissal: promotion when most open templates are marketing ones.
export const caseLegitLabel = (dossier) => {
  const open = allItems(dossier || {}).filter(isOpenItem);
  const promo = open.filter((i) => legitFor(i.template?.category) !== "benign").length;
  return open.length && promo * 2 >= open.length ? "ordinary_commercial_promotion" : "benign";
};

const templateButtons = (item) =>
  (item.template?.components || [])
    .flatMap((c) => [
      c,
      ...((c && c.cards) || []).flatMap((card) => (card && card.components) || []),
    ])
    .filter((c) => String(c?.type || "").toUpperCase() === "BUTTONS")
    .flatMap((c) => c.buttons || []);

// A button whose resolved destination is a different site than its URL.
export const realDestination = (button) => {
  if (!button?.url || !button.resolved_url) return null;
  const from = hostOf(button.url);
  const to = hostOf(button.resolved_url);
  return to && to !== from ? to : null;
};

const joinList = (xs) =>
  xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;

// Why an item got its decision. The content label alone decides only up to
// "report"; review / block on an unsure or legit label comes from a Meta
// policy category, low trust or a forced-review rule.
const CLEAN_LABELS = new Set(["uncertain", "benign", "ordinary_commercial_promotion"]);
// The label itself doesn't call the content harmful.
export const isCleanLabel = (label) => !label || CLEAN_LABELS.has(label);
const FINE_LABELS = new Set(["benign", "ordinary_commercial_promotion"]);

const ESCALATING_TIERS = new Set(["review", "block"]);
// Plain causes: "it may fall under a restricted Meta category (payday / P2P
// lending)", "the account's trust is low". `they` for a group of items.
export function itemCauses(item, forced = [], they = false) {
  const cats = (item?.policy?.categories || []).filter((cat) => cat && (!cat.tier || ESCALATING_TIERS.has(cat.tier)));
  const names = (list) => [...new Set(list.map((cat) => humanizePolicy(cat.id || cat)).filter(Boolean))];
  const blocked = names(cats.filter((cat) => cat.tier === "block"));
  const restricted = names(cats.filter((cat) => cat.tier !== "block")).filter((n) => !blocked.includes(n));
  const it = they ? "they" : "it";
  const causes = [];
  if (blocked.length) causes.push(`${it} fall${they ? "" : "s"} under a blocked Meta category (${joinList(blocked)})`);
  if (restricted.length) causes.push(`${it} may fall under a restricted Meta category (${joinList(restricted)})`);
  if (item?.trust?.tier === "LOW") causes.push("the account's trust is low");
  if (!causes.length && forced.length) {
    causes.push(`a rule forces a human check (${joinList(forced.map((f) => humanizeFeature(f).toLowerCase()))})`);
  }
  return causes;
}

// The "Flagged because:" line of an item.
export function itemFlagReason(item, forced = []) {
  const label = item?.content?.primaryLabel;
  const causes = itemCauses(item, forced);
  if (!isCleanLabel(label)) {
    const harm = `the content looks harmful (${humanizeLabel(label).toLowerCase()})`;
    return causes.length ? `${harm}; also ${joinList(causes)}.` : `${harm}.`;
  }
  if (!causes.length) {
    if (label === "uncertain") return "the checker was unsure about the content.";
    return item?.decision === "report" ? "it was logged for a second look." : "it was sent for a human check.";
  }
  const policyOnly = causes.every((x) => x.includes("Meta category"));
  if (FINE_LABELS.has(label) && policyOnly) {
    return `${joinList(causes)} — that's the only reason; the content itself looks fine.`;
  }
  const lead = label === "uncertain" ? "the checker is unsure about the content and " : "";
  return `${lead}${joinList(causes)}.`;
}

// What happened to the item, in the reviewer's words:
// { key, label, tone, hint, help, term, open }.
const RESOLVED = {
  submitted: { label: "Approved — sent to Meta", tone: "done" },
  meta_rejected: { label: "Approved — Meta rejected it", tone: "bad" },
  rejected: { label: "Rejected — never sent", tone: "report" },
  template_gone: { label: "Approved — template no longer exists", tone: "report" },
  ok: { label: "Marked OK", tone: "done" },
  deleted: { label: "Template deleted", tone: "report" },
  suspended: { label: "Resolved by suspension", tone: "report" },
};
export const heldOutcome = (held) =>
  held ? held.outcome || (held.rejected ? "rejected" : held.releasedAt ? "submitted" : null) : null;

export function itemStatus(item) {
  if (!item) return null;
  const key = item.decision === "block" ? (isHeld(item) ? "held" : "block") : item.decision;
  const base = STATUS_HELP[key];
  if (isOpenItem(item)) {
    return base ? { key, ...base, tone: key, open: true } : null;
  }
  const r = RESOLVED[heldOutcome(item.held)] || RESOLVED[item.review?.status];
  const was = item.held ? STATUS_HELP.held : base;
  const label = r ? r.label : titleCase(item.review?.status || "reviewed");
  return {
    key: "resolved",
    label,
    tone: r ? r.tone : "report",
    help: `Reviewed: ${label.toLowerCase()}.${was ? ` It was “${was.label}” before review.` : ""}`,
    term: was ? `${was.term} → ${heldOutcome(item.held) || item.review?.status || "reviewed"}` : null,
    open: false,
  };
}

// Owner email domain, and whether every flagged link stays on it (or on the
// project website): easyloans.cready.in belongs to cready.in.
const FREE_MAIL = new Set(["gmail.com", "yahoo.com", "yahoo.in", "hotmail.com", "outlook.com", "live.com", "icloud.com", "rediffmail.com", "proton.me", "protonmail.com"]);
const onDomain = (host, domain) => !!domain && (host === domain || host.endsWith(`.${domain}`));
export function senderDomain(dossier) {
  const email = String(dossier?.owner?.email || "");
  const at = email.lastIndexOf("@");
  const mail = at > 0 ? email.slice(at + 1).toLowerCase().replace(/^www\./, "") : "";
  const domain = mail && !FREE_MAIL.has(mail) ? mail : "";
  const website = hostOf(dossier?.project?.website) || "";
  const items = allItems(dossier || {});
  const open = items.filter(isOpenItem);
  const hosts = new Set();
  (open.length ? open : items).forEach((item) => {
    (item.content?.hosts || []).forEach((h) => hosts.add(hostOf(h)));
    templateButtons(item).forEach((b) => {
      hosts.add(hostOf(b.resolved_url));
      hosts.add(hostOf(b.url));
    });
  });
  const links = [...hosts].filter((h) => h && !TRACKER_HOSTS.has(h));
  const roots = [domain, website].filter(Boolean);
  const owners = links.length
    ? links.map((h) => roots.filter((r) => onDomain(h, r)).sort((a, b) => a.length - b.length)[0])
    : [];
  const ownLinks = owners.length && owners.every(Boolean) ? [...new Set(owners)] : [];
  return { domain, ownLinks };
}

// A sentence is a list of parts: strings, or { b: "bold text" }.
const join = (parts) => {
  const out = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(i === parts.length - 1 ? " and " : ", ");
    out.push({ b: p });
  });
  return out;
};

export function buildWhy(dossier) {
  if (!dossier) return [];
  const items = allItems(dossier);
  const open = items.filter(isOpenItem);
  const sentences = [];

  // 1. What happened, grouped by status and cause: "Two templates are held —
  // not sent to Meta — because they may fall under a restricted Meta category
  // (payday / P2P lending). One was sent to Meta and needs a check because ..."
  const nounFor = (list) => {
    const types = [...new Set(list.map((i) => i.subjectType))];
    if (types.length > 1) return list.length === 1 ? "item" : "items";
    const s = SUBJECTS.find((x) => x.key === types[0]) || SUBJECTS[0];
    return list.length === 1 ? s.single : s.plural;
  };
  const forced = dossier.trustBreakdown?.forcedReview || [];
  const statusKey = (item) => (item.decision === "block" ? (isHeld(item) ? "held" : "block") : item.decision || "review");
  // Reason after "because", for one item or a group of them.
  const reasonFor = (item, they) => {
    const label = item.content?.primaryLabel;
    const causes = itemCauses(item, forced, they);
    if (!causes.length) return label === "uncertain" ? "the checker was unsure about the content" : "a human check was requested";
    const lead = FINE_LABELS.has(label) ? "the content looks fine, but " : label === "uncertain" ? "the checker is unsure about the content and " : "";
    return `${lead}${joinList(causes)}`;
  };
  const groups = new Map();
  open.forEach((item) => {
    const status = statusKey(item);
    const label = item.content?.primaryLabel;
    const harm = !isCleanLabel(label);
    const key = `${status}|${harm ? "harm" : reasonFor(item, false)}`;
    const g = groups.get(key) || { status, harm, items: [], labels: new Set() };
    g.items.push(item);
    if (harm) g.labels.add(humanizeLabel(label).toLowerCase());
    groups.set(key, g);
  });
  const ORDER = ["held", "block", "review", "report"];
  const list = [...groups.values()].sort(
    (a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || b.items.length - a.items.length
  );
  if (list.length) {
    const shown = list.slice(0, 3);
    const rest = list.slice(3).reduce((n, g) => n + g.items.length, 0);
    const clauses = shown.map((g, i) => {
      const n = g.items.length;
      const one = n === 1;
      const reason = g.harm ? `the content looks like ${joinList([...g.labels])}` : reasonFor(g.items[0], !one);
      const head = i === 0 ? `${cap(numWord(n))} ${nounFor(g.items)}` : cap(numWord(n));
      const was = one ? "was" : "were";
      const what = {
        held: `${one ? "is" : "are"} held — not sent to Meta —`,
        block: `${was} sent to Meta but ${one ? "is" : "are"} high risk:`,
        review: `${was} sent to Meta and need${one ? "s" : ""} a check:`,
        report: `${was} sent to Meta and only logged (FYI):`,
      }[g.status];
      return `${head} ${what}${g.status === "held" ? " because" : ""} ${reason}.`;
    });
    sentences.push([`${clauses.join(" ")}${rest ? ` ${cap(numWord(rest))} more for other reasons.` : ""}`]);
  } else if (dossier.violationCount) {
    sentences.push([`Nothing is open now; ${numWord(dossier.violationCount)} violation${dossier.violationCount === 1 ? " is" : "s are"} on record.`]);
  }

  // 2. Real destinations behind tracked or shortened links.
  const scan = open.length ? open : items;
  const tracked = new Set();
  const dest = new Set();
  scan.forEach((item) => {
    templateButtons(item).forEach((b) => {
      const to = realDestination(b);
      if (to) {
        dest.add(to);
        if (TRACKER_HOSTS.has(hostOf(b.url))) tracked.add(to);
      }
    });
  });
  scan.forEach((item) => {
    const own = new Set(templateButtons(item).map((b) => hostOf(b.url)));
    (item.content?.hosts || []).forEach((h) => {
      const host = hostOf(h);
      if (host && !TRACKER_HOSTS.has(host) && !own.has(host)) dest.add(host);
    });
  });
  if (dest.size) {
    const hosts = [...dest].slice(0, 3);
    sentences.push(
      tracked.size
        ? ["Its links go through our tracker to ", ...join(hosts), "."]
        : ["Its links lead to ", ...join(hosts), "."]
    );
  }

  // 3. Shared device and already-suspended neighbours.
  const devices = dossier.devices || [];
  const maxAccounts = devices.reduce((m, d) => Math.max(m, d.accountCount || 0), 0);
  if (maxAccounts >= 2) {
    const suspended = new Set(
      devices.flatMap((d) => (d.linkedAccounts || []).filter((a) => a.suspended).map((a) => a.assistantId))
    ).size;
    sentences.push([
      `It was created from a device used by ${numWord(maxAccounts)} accounts${
        suspended ? `, ${numWord(suspended)} already suspended` : ""
      }.`,
    ]);
  }

  // 4. Fresh owner / WABA.
  const ownerAge = ageDays(dossier.owner?.createdOn);
  const wabaAge = ageDays(dossier.project?.wabaActivatedOn);
  const young = [];
  if (ownerAge != null && ownerAge < 7) young.push(`the owner signed up ${fromNow(dossier.owner.createdOn)}`);
  if (wabaAge != null && wabaAge < 7) young.push(`its WhatsApp number was activated ${fromNow(dossier.project.wabaActivatedOn)}`);
  if (young.length) sentences.push([`${cap(joinList(young))}.`]);

  // 5. Rules that force a human look.
  if (forced.length) {
    sentences.push([`A rule forces a human check: ${joinList(forced.map((f) => humanizeFeature(f).toLowerCase()))}.`]);
  }
  // Keep it short; forced-review flags also show under TRUST.
  return sentences.slice(0, 4);
}

const ACTION_TEXT = {
  ok: "Marked OK",
  delete: "Deleted template",
  suspend_project: "Suspended project",
  suspend_account: "Suspended account",
};

const HELD_TEXT = {
  submitted: "Approved — sent to Meta",
  meta_rejected: "Approved — Meta rejected it",
  template_gone: "Approved — template no longer exists",
  rejected: "Rejected — never sent",
};

// Review events; a case-level decision touches many items with the same
// reviewer / time / action, so those collapse into one line.
export function buildHistory(dossier) {
  if (!dossier) return [];
  const groups = new Map();
  allItems(dossier).forEach((item) => {
    const r = item.review;
    if (!r || r.status === "open" || !r.at) return;
    const fate = heldOutcome(item.held) || "";
    const key = [r.by, r.at, r.action || r.status, r.label || "", r.note || "", fate].join("|");
    const g = groups.get(key) || { at: r.at, by: r.by, review: r, fate, names: [] };
    g.names.push(item.templateName || item.template?.name || "item");
    groups.set(key, g);
  });
  const events = [...groups.values()].map((g) => ({
    at: g.at,
    by: g.by,
    action: `${HELD_TEXT[g.fate] || ACTION_TEXT[g.review.action] || titleCase(g.review.status)}${
      g.review.label ? ` as ${labelWord(g.review.label)}` : ""
    } · ${
      g.names.length === 1 ? g.names[0] : `${g.names.length} items`
    }`,
    note: g.review.note,
    danger: g.review.status !== "ok" || g.fate === "rejected" || g.fate === "meta_rejected",
  }));
  const s = dossier.suspension;
  if (s?.at) {
    events.push({
      at: s.at,
      by: s.by,
      action: `Suspended (${s.scope || "project"})`,
      note: s.note,
      danger: true,
    });
  }
  const p = dossier.project || {};
  if (p.reinstatedAt) events.push({ at: p.reinstatedAt, by: p.reinstatedBy, action: "Reinstated", note: null });
  return events.sort((a, b) => new Date(b.at) - new Date(a.at));
}
